import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { TimeoutError, catchError, throwError, timeout } from 'rxjs';
import { REQUEST_TIMEOUT_MESSAGE } from '../errors/business-error-messages';
import { REQUEST_TIMEOUT_BUDGET } from '../http/request-timeout';

/**
 * Timeout Interceptor — bounds how long an HTTP request may take.
 *
 * ## Why
 *
 * `HttpClient` has no time bound of its own: against a stalled backend a request
 * waits on the browser's socket behaviour, which can be minutes or effectively
 * forever, leaving a spinner that never resolves and a flow that never aborts.
 *
 * The bound is **per request attempt**, not per user-visible wait. Callers that
 * re-issue requests stack budgets by design: `transaction-column.store.ts`
 * retries twice (so up to 3 attempts) and `topup.store.ts` polls up to 3 times.
 * `timeout({ each })` also measures the gap *between* events, so a response that
 * keeps emitting progress events is never cut off — desirable, and worth knowing
 * before quoting a hard ceiling on total wall-clock.
 *
 * ## Ordering
 *
 * Registered BEFORE `authInterceptor` in `app.config.ts`, so it is the outermost
 * interceptor and its budget spans the whole downstream chain — including the
 * 401 refresh-and-replay. That placement is what bounds the worst hang in the
 * app: a request that 401s while a refresh is in flight parks on
 * `TokenRefreshService.token$`, and because the queue filters out `null`, a
 * refresh that fails or never settles leaves that request waiting forever with
 * no error. Inside `authInterceptor` the timeout would apply per network attempt
 * and leave the queue unbounded.
 *
 * The trade-off is accepted: a request that 401s late in its budget has little
 * left for the retry. The guarantee being bought is about total user-visible
 * wait, so it has to be measured that way.
 *
 * ## Why a synthetic HttpErrorResponse
 *
 * A bare RxJS `TimeoutError` would reach ~108 `catchError` blocks that are typed
 * `(error: HttpErrorResponse)` and branch on `error.status` — arriving with
 * `status` and `error` both undefined, silently defeating every one of them.
 * Converting to an `HttpErrorResponse` carrying the copy under `error_message`
 * makes a timeout flow through machinery that already exists: `extractErrorMessage`
 * prefers `error.error.error_message` and only suppresses bodies at status >= 500,
 * so at 408 it surfaces the message verbatim — in every store and API that uses
 * it, with no changes to any of them.
 *
 * It also stays inert in `authInterceptor`: the status is not 401, and the body
 * carries no `error_code`, so the unmapped-code Sentry report skips it.
 */
export const timeoutInterceptor: HttpInterceptorFn = (req, next) => {
  const budgetMs = req.context.get(REQUEST_TIMEOUT_BUDGET);

  // Anything not finite-and-positive disables the timeout. The `Number.isFinite`
  // half is not defensive noise: `timeout({ each: Infinity })` reaches
  // `setInterval`, whose delay is coerced to a 32-bit int, so `Infinity` becomes
  // 0 and times out EVERY such request immediately — the exact opposite of what a
  // caller writing `Infinity` to mean "no timeout" intends. Verified: removing
  // `Number.isFinite` makes the `Infinity` case in the spec fail. NaN happens to
  // be inert today and is covered for symmetry, not because it misbehaves.
  if (!Number.isFinite(budgetMs) || budgetMs <= 0) {
    return next(req);
  }

  return next(req).pipe(
    // `each`, not the bare `timeout(ms)` overload: a response can arrive as
    // several events (sent → download progress → response), and `each` resets
    // the clock per event. The bare overload bounds only the FIRST emission,
    // which would let a stalled multi-event response run on unbounded.
    timeout({ each: budgetMs }),
    catchError((error: unknown) => {
      if (!(error instanceof TimeoutError)) {
        return throwError(() => error);
      }
      return throwError(() => toTimeoutResponse(req, budgetMs));
    }),
  );
};

/**
 * Build the synthetic `HttpErrorResponse` that stands in for the response we
 * never received.
 *
 * Status 408 (Request Timeout) rather than 0: 0 means "network failure" and is
 * already checked for elsewhere, and 408 keeps the error below the 5xx cutoff
 * that makes `extractErrorMessage` discard the body.
 *
 * The body carries `error_message` ONLY, deliberately not `message`. The app has
 * two error-body conventions: the wallet APIs send `error_code`/`error_message`
 * (read by `extractErrorMessage`), while `ErrorHandler.parseError` reads the
 * `ApiError` shape, `code`/`message`. `parseError` maps status 408 to
 * `type: 'network'`, and its consumers — `login.ts`, `forgot-password.ts` — render
 * their own translated `t.networkError` for that type and ignore the body message.
 * Those are the only i18n'd error surfaces in the app, so localized copy there is
 * better than the French literal below. Adding a `message` key would not change
 * what they show today, but it would leak French into an English UI the moment
 * one of them starts honouring it. Leave it out.
 */
function toTimeoutResponse(request: HttpRequest<unknown>, budgetMs: number): HttpErrorResponse {
  return new HttpErrorResponse({
    status: 408,
    statusText: 'Request Timeout',
    url: request.url,
    // `error_message` is the key `extractErrorMessage` reads first, so this copy
    // reaches the user through the app's normal error path untouched.
    error: { error_message: REQUEST_TIMEOUT_MESSAGE, timeout_ms: budgetMs },
  });
}
